import { Recommendation } from '@agroone/entities'
import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { catchError, debounceTime, forkJoin, map, mergeMap, of, tap } from 'rxjs'

import { RecommendationService } from '../../../../../shared/recommendation/services/recommendation.service'
import {
  addRecommendation,
  addRecommendationFailure,
  addRecommendationSuccess,
  addRecommendations,
  addRecommendationsFailure,
  addRecommendationsSuccess,
  deleteRecommendation,
  deleteRecommendationError,
  deleteRecommendationSuccess,
  loadRecommendations,
  loadRecommendationsFailure,
  loadRecommendationsSuccess,
} from '../actions/recommendation.actions'
import { CropDetailState } from '../reducers'

@Injectable()
export class RecommendationEffects {
  private actions$ = inject(Actions)
  private recommendationService = inject(RecommendationService)
  private store = inject<Store<CropDetailState>>(Store)

  public getRecommendationsByCropId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRecommendations),
      debounceTime(100),
      mergeMap((action) =>
        this.recommendationService.getByCropId(action.cropId).pipe(
          map((recommendations) =>
            loadRecommendationsSuccess({
              cropId: action.cropId,
              recommendations,
            }),
          ),
          catchError((e) => of(loadRecommendationsFailure({ error: `${e}` }))),
        ),
      ),
    ),
  )

  public addRecommendation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addRecommendation),
      mergeMap(({ payload }) =>
        this.recommendationService.add(payload.recommendation, { isMultiReco: false }).pipe(
          map((recommendationResponse) =>
            addRecommendationSuccess({
              payload: {
                recommendations: recommendationResponse,
              },
            }),
          ),
          catchError((e) =>
            of(
              addRecommendationFailure({
                error: `${e}`,
                message: `An error occurred during recommendation creation`,
              }),
            ),
          ),
        ),
      ),
    ),
  )

  public addRecommendations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addRecommendations),
      mergeMap(({ payload }) => {
        const isMultiReco = payload.isMultiReco ?? (payload.recommendations?.length ?? 0) > 1
        return forkJoin(
          payload.recommendations.map((item: Recommendation) => this.recommendationService.add(item, { isMultiReco })),
        ).pipe(
          map((results: unknown[]) =>
            results.flatMap((r) => (Array.isArray(r) ? (r as Recommendation[]) : [r as Recommendation])),
          ),
          tap((flatResults: Recommendation[]) => {
            this.store.dispatch(
              addRecommendationSuccess({
                payload: {
                  recommendations: flatResults,
                },
              }),
            )
          }),
          map((flatResults: Recommendation[]) =>
            addRecommendationsSuccess({
              payload: {
                recommendations: flatResults,
              },
            }),
          ),
          catchError((error) =>
            of(
              addRecommendationsFailure({
                error,
                message: '',
              }),
            ),
          ),
        )
      }),
    ),
  )

  public deleteRecommendation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteRecommendation),
      mergeMap(({ cropId, recommendationId }) =>
        this.recommendationService.delete(recommendationId).pipe(
          map(() => deleteRecommendationSuccess({ cropId, recommendationId })),
          catchError((e) => of(deleteRecommendationError({ error: `${e}` }))),
        ),
      ),
    ),
  )
}
